import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowRightIcon, StarIcon } from 'lucide-react';
import { brand, images } from '../data/site';
const EASE = [0.23, 1, 0.32, 1] as const;
interface HeroProps {
  media: 'video' | 'image';
}
export function Hero({
  media
}: HeroProps) {
  const ref = useRef<HTMLElement | null>(null);
  const reduce = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', reduce ? '0%' : '12%']);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  return <section id="top" ref={ref} className="relative flex h-[100svh] min-h-[600px] w-full flex-col justify-end overflow-hidden bg-espresso" data-id="element-38" data-mp="sd48ct8z2sk9">
      <motion.div style={{
      y
    }} className="absolute inset-0 h-[112%]" data-id="element-39" data-mp="3d33eqyadctkr">
        {media === 'video' && !reduce ? <video className="h-full w-full object-cover" src={brand.video} poster={images.poster} autoPlay muted loop playsInline data-id="element-40" data-mp="384jt6yzotjsx" /> : <img src={images.poster} alt="" className="h-full w-full object-cover" data-id="element-41" data-mp="39auqrc9b5kwp" />}
      </motion.div>
      <div className="absolute inset-0 bg-espresso/55" aria-hidden="true" data-id="element-42" data-mp="3e8mud3jbevgq" />

      <motion.div style={{
      opacity: fade
    }} className="relative z-10 mx-auto w-full max-w-6xl px-6 pb-14 lg:pb-20" data-id="element-43" data-mp="30hrg1bq1rm5r">
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: EASE,
        delay: 0.1
      }} className="text-sm text-cream/70" data-id="element-44" data-mp="35a2fyeksf7yb">
          Viennoiserie &amp; koffie · Amsterdam Nieuw-West
        </motion.p>

        <h1 className="mt-5 text-cream" data-id="element-45" data-mp="2hjyof5kviyno">
          {['Layers of gold,', 'moments of joy.'].map((line, i) => <span key={line} className="block overflow-hidden" data-id="element-46" data-mp="2lmuyg7wclmg0" data-mpi={i}>
              <motion.span initial={{
            y: '105%'
          }} animate={{
            y: '0%'
          }} transition={{
            duration: 0.3,
            ease: EASE,
            delay: 0.14 + i * 0.06
          }} className="block text-[clamp(2.6rem,7vw,5.5rem)] font-semibold leading-[1.02] tracking-[-0.03em]" data-id="element-47" data-mp="s2tffr2zujsd">
                {line}
              </motion.span>
            </span>)}
        </h1>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: EASE,
        delay: 0.28
      }} className="mt-10 flex flex-col gap-8 border-t border-cream/20 pt-8 lg:flex-row lg:items-end lg:justify-between" data-id="element-48" data-mp="1b0du65c1o1z9">
          <p className="max-w-md text-lg leading-relaxed text-cream/75" data-id="element-49" data-mp="x0l8iofdkby5">
            Dagvers gebakken viennoiserie en ambachtelijke koffie op de Burgemeester de Vlugtlaan.
            Alles in-house gemaakt en 100% halal.
          </p>

          <div className="flex flex-wrap items-center gap-4" data-id="element-50" data-mp="1x9b6f48dr7e5">
            <a href="#app" className="group inline-flex items-center gap-2.5 rounded-full bg-cream px-7 py-3.5 text-sm font-medium text-ink transition-colors duration-150 ease-out hover:bg-clay hover:text-cream" data-id="element-51" data-mp="1xb5pse406rg4">
              Download de app
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" data-id="element-52" data-mp="2b1hjghg8ky9d" />
            </a>
            <a href="#kaart" className="rounded-full border border-cream/40 px-7 py-3.5 text-sm font-medium text-cream transition-colors duration-150 ease-out hover:bg-cream/10" data-id="element-53" data-mp="2g256ks1cguyb">
              Bekijk de kaart
            </a>
            <span className="flex items-center gap-2 text-sm text-cream/70" data-id="element-54" data-mp="2tz1xsuldkefq">
              <StarIcon className="h-4 w-4 fill-clay text-clay" aria-hidden="true" data-id="element-55" data-mp="unj1bfh9cymy" />
              {brand.rating} · {brand.reviewCount} reviews
            </span>
          </div>
        </motion.div>
      </motion.div>
    </section>;
}